import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import moment from 'moment';
import { isMobileOnly } from 'react-device-detect';
import DatePicker from 'react-datepicker';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PDFExport, savePDF } from '@progress/kendo-react-pdf';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import Notification from 'rc-notification';
import SmallLoader from '../../../../shared/components/SmallLoader';
import {
    getRevenueFromCustomerData, handleLoader
} from '../../../../redux/actions/RevenueFromCustomerAction';
import { findMonthsBetweenDates, setCurrencyFormat } from '../../../../utils/helpers';
import {
    vendorNameMapping, sortType
} from '../../helper';
import RevenueCustomerTable from './RevenueCustomerTable';

const RevenueFromCustomer = ({ dispatch, vendor }) => {

    const location = useLocation();
    const { isLoading, revenueFromCustomerData, salesChannelFilterData } = useSelector(
        ({ revenueFromCustomerReducer, marketPlaceReducer }) =>
        ({
            salesChannelFilterData: marketPlaceReducer.salesChannel,
            revenueFromCustomerData: revenueFromCustomerReducer?.[vendor]?.revenueFromCustomerData,
            isLoading: revenueFromCustomerReducer?.[vendor]?.isLoading,
        })
    );

    const [revenueFromCustomer, setWholeSaleRevFromCustomerToShow] = useState([]);
    const [filterData, setFilterData] = useState({});
    const [dateRange, setDateRange] = useState([
        new Date(moment().subtract(6, 'months')),
        new Date(moment()),
    ]);

    const DATE_FORMAT = 'MMM-YYYY';

    const [startDate, endDate] = dateRange;
    const monthsBetween = findMonthsBetweenDates(startDate, endDate, DATE_FORMAT);

    const filterBasedOnFilter = (item) => {
        const filters = Object.keys(filterData);
        let flag = true
        // eslint-disable-next-line no-restricted-syntax
        for (const i of filters) {
            const filter = filterData[i];
            const filtersArray = filter?.reduce((acc, fil) => [...acc, fil?.value], []) ?? [];
            if (filtersArray?.length && !filtersArray.includes(item?.[i]?.toLowerCase())) {
                flag = false
            }
        }

        return flag;
    }

    const container = React.useRef(null);
  const exportToPDF = () => {
    // console.log('Hello')
    Notification.newInstance({}, (notification) => {
      notification.notice({
        content: 'PDF loading......',
        duration: 5,
        style: {top: 120, fontWeight: "bold"},
        maxCount: 10
      });
    });
    const dvTb = document.getElementById('dvTable');
    dvTb.style.display = "none";  
    const element = container.current || document.body;
    
    savePDF(element, {
      paperSize: "auto",
      Header: "test",
      margin: { left: "1cm", top: "1cm", right: "1cm", bottom: "1cm" },
     // margin: "2cm",
     // forcePageBreak: ".page-break",
    //   landscape: true,
    //  multiPage: true,
      fileName: `${vendorNameMapping[vendor]} Revenue From Customers Report ${new Date().getFullYear()}`,
    })
    dvTb.style.display = "block";  
  }


    const callApi = () => {
        if (vendor) {
            const data = {
                vendor, fromDate: moment(startDate).format('YYYY-MM-DD'),
                toDate: moment(endDate).format('YYYY-MM-DD'),
            }
            dispatch(handleLoader(true, vendor))
            dispatch(getRevenueFromCustomerData(data));
        }
    }

    const handleApplyDate = () => {
        callApi();
    }

    useEffect(() => {
        dispatch(handleLoader(true));
        callApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setWholeSaleRevFromCustomerToShow(revenueFromCustomerData?.modal?.data || []);
    }, [revenueFromCustomerData]);

    useEffect(() => {
        const OptChennel = 'salesChannel';
        setFilterData({ ...filterData, [OptChennel]: salesChannelFilterData });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesChannelFilterData])

    const revenueFromCustomerForDatesSelected = revenueFromCustomer?.filter(item =>
        filterBasedOnFilter(item));

    const tableHeaderData = [{
        Header: 'Customer',
        accessor: 'customer',
        disableGlobalFilter: false,
        rightAlign: false,
        width: 180,
        sortType,
        Footer: 'Total'
    }];

    const initializeMonthWithData = {}

    monthsBetween?.forEach((month) => {
        initializeMonthWithData[month] = 0
        tableHeaderData.push({
            Header: month,
            accessor: month,
            disableGlobalFilter: false,
            rightAlign: true,
            width: 150,
            Cell: ({ cell: { row: { original } } }) =>
                // eslint-disable-next-line react/prop-types
                setCurrencyFormat(Number(original[month] || 0).toFixed(original[month] ? 2 : 0), true),
            sortType,
            Footer: (info) => {
                const { rows } = info;
                const total = React.useMemo(
                    () =>
                        rows.reduce((sum, row) => row.values[month] + sum, 0),
                    [rows]
                )
                return <span className="text-right d-block">{setCurrencyFormat(Number(total).toFixed(2), true)}</span>
            },
        })
    });

    const dataToShow = {};

    revenueFromCustomerForDatesSelected.reduce((acc, item) => {
        const { salesYear, salesMonthStr, customerName, amount } = item;

        const date = `${salesMonthStr}-${salesYear}`;

        if (acc[customerName]) {
            acc[customerName] = {
                ...acc[customerName],
                [date]: Number(amount)
            }
        } else {
            acc[customerName] = {
                ...initializeMonthWithData,
                customer: customerName,
                [date]: Number(amount)
            }
        }
        return acc
    }, dataToShow);

    const tableData = Object.values(dataToShow);

    return (
        <Container className="dashboard">
            <Row className="sales-breakdown">
                <Col sm="12">
                    <Card className="pb-3">
                        <CardBody className="min-w-50 p-3">
                            <div className="card__title mb-2">
                                <h5 className="bold-text card__title-center">
                                    {vendorNameMapping[vendor]} Revenue From Customers </h5>
                            </div>
                            <Row className="align-items-center">
                                <Col sm="4">
                                    <div className="form__form-group mb-0">
                                        <span className="form__form-group-label">Date</span>
                                        <div className="form__form-group-field">
                                            <div className="date-picker">
                                                <DatePicker
                                                    dateFormat="MM/dd/yyyy"
                                                    selectsRange
                                                    startDate={startDate}
                                                    endDate={endDate}
                                                    onChange={(update) => {
                                                        setDateRange(update);
                                                    }}
                                                    withPortal={isMobileOnly}
                                                    value={`${moment(startDate)
                                                        .format('MM/DD/YYYY')} - ${endDate ?
                                                            moment(
                                                                endDate,
                                                            ).format('MM/DD/YYYY') : 'Select End Date'}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col sm="8" style={{ top: '10px' }}>
                                    <button
                                        className="btn btn-sm btn-primary apply-date-button"
                                        type="button"
                                        onClick={handleApplyDate}
                                    >
                                        Apply
                                    </button>
                                    <button className="btn btn-sm btn-primary
                                             apply-date-button" type="button"
                                             title='Export to pdf' disabled={isLoading}
                                    onClick={exportToPDF}>Export PDF</button>
                                </Col>                               
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <div id="divToPrint" ref={container}>
            <Row className="show-sales-region">
                <Col sm="12">
                    <Card>
                        <CardBody>
                            <div className="top-location" style={{ minHeight: '400' }}>
                                <div className="card__title row align-items-center">
                                    <h5 className="bold-text col-md-4">Revenue From Customers</h5>
                                </div>
                                <div >
                                    {isLoading ? <div style={{ minHeight: '200px' }}
                                        className="row align-items-center justify-content-center">
                                        <SmallLoader loading={isLoading} />
                                    </div>
                                        : <RevenueCustomerTable
                                            fileName={`${vendorNameMapping[vendor]} Revenue From Customer Report`}
                                            tableHeaderData={tableHeaderData}
                                            data={tableData}
                                            vendor={vendor} />}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            </div>
        </Container>
    );
};

RevenueFromCustomer.propTypes = {
    dispatch: PropTypes.func.isRequired,
    vendor: PropTypes.number.isRequired
};

export default connect(state => ({
    rtl: state.rtl,
}))(RevenueFromCustomer);
